/**
 * Renaming a column, and everything that has to follow it.
 *
 * A field id is not a label — it is what expressions, filters and sorts *point
 * at*. Changing one without rewriting its references does not fail loudly; it
 * leaves a filter matching nothing and an expression resolving to null, which
 * looks like the data being wrong. So a rename is a refactor, and it is done
 * here in one pure function rather than in the component that happens to own
 * the input.
 *
 * Only a **computed** column's id is the user's to change. A server column's id
 * has to match the Arrow schema it arrives under, so renaming one would break
 * the binding rather than move it.
 */

import type {
  ScreenerColumn,
  ScreenerConfig,
  ScreenerExpr,
  ScreenerFilterNode,
} from "@qufolio/model";

/** Is this a legal column id — the shape the DSL lexer accepts as one token? */
export function isValidFieldId(id: string): boolean {
  return /^[A-Za-z_]\w*$/.test(id);
}

export type RenameProblem = "empty" | "invalid" | "taken";

/**
 * Why a rename cannot happen, or null when it can.
 *
 * `taken` covers server columns too: a computed column that shadows a real
 * field id would make the same name mean two things depending on where you
 * looked.
 */
export function renameProblem(
  config: ScreenerConfig,
  from: string,
  to: string,
  serverFields: readonly string[] = [],
): RenameProblem | null {
  const next = to.trim();
  if (!next) return "empty";
  if (!isValidFieldId(next)) return "invalid";
  if (next === from) return null;
  if (serverFields.includes(next)) return "taken";
  if ((config.columns ?? []).some((c) => c.field === next)) return "taken";
  return null;
}

function renameInExpr(expr: ScreenerExpr, from: string, to: string): ScreenerExpr {
  if (expr == null || typeof expr !== "object" || !("kind" in expr)) return expr;
  if (expr.kind === "field") {
    if (expr.field !== from) return expr;
    const ref = expr.ref ? { ...expr.ref, base: expr.ref.base === from ? to : expr.ref.base } : undefined;
    return { ...expr, field: to, ...(ref ? { ref } : {}) };
  }
  if (expr.kind === "binary") {
    return {
      ...expr,
      left: renameInExpr(expr.left, from, to),
      right: renameInExpr(expr.right, from, to),
    };
  }
  if (expr.kind === "call") {
    return { ...expr, args: expr.args.map((a) => renameInExpr(a, from, to)) };
  }
  return expr;
}

/**
 * `filter` is either a structured node or a raw expression — the contract's two
 * authoring surfaces — so both arms are handled here rather than at the call
 * site.
 */
type Filter = NonNullable<ScreenerConfig["filter"]>;

function renameInFilter(node: Filter, from: string, to: string): Filter {
  if ("children" in node) {
    return {
      ...node,
      children: node.children.map((c) => renameInFilter(c, from, to) as ScreenerFilterNode),
    };
  }
  if ("kind" in node) return renameInExpr(node, from, to);
  return node.field === from ? { ...node, field: to } : node;
}

/** Does this DSL text mention `id` as a whole word? */
function mentions(source: string, id: string): boolean {
  return new RegExp(`(^|[^\\w])${id}([^\\w]|$)`).test(source);
}

function renameInColumn(column: ScreenerColumn, from: string, to: string): ScreenerColumn {
  const next: ScreenerColumn = column.field === from ? { ...column, field: to } : { ...column };
  if (!column.computed) return next;

  const expression = renameInExpr(column.computed.expression, from, to);
  const computed = {
    ...column.computed,
    expression,
    ...(column.computed.requires
      ? { requires: column.computed.requires.map((f) => (f === from ? to : f)) }
      : {}),
  };

  // The stored text is dropped rather than rewritten when it mentions the old
  // name. A textual substitution would also hit the id inside a string literal
  // — `sector == "computed1"` — and leaving it stale is worse still: the editor
  // would reopen showing text that no longer compiles. Without `source` the
  // editor reprints from the tree, which is always correct.
  if (computed.source && mentions(computed.source, from)) delete computed.source;

  next.computed = computed;
  if (next.colorBy) next.colorBy = renameInExpr(next.colorBy, from, to);
  return next;
}

/**
 * Rename a column and every reference to it.
 *
 * Columns, their expressions and `requires`, conditional-format expressions,
 * the base filter and the sort keys — everything the contract lets point at a
 * field id.
 */
export function renameColumn(config: ScreenerConfig, from: string, to: string): ScreenerConfig {
  if (from === to) return config;
  return {
    ...config,
    ...(config.columns ? { columns: config.columns.map((c) => renameInColumn(c, from, to)) } : {}),
    ...(config.filter ? { filter: renameInFilter(config.filter, from, to) } : {}),
    ...(config.sort
      ? { sort: config.sort.map((s) => (s.field === from ? { ...s, field: to } : s)) }
      : {}),
  };
}
